/**
 * Every version of one artifact (task 828).
 *
 * An artifact is the identity that survives revision, so "which versions are
 * there, and can I get the one from before the last revision" is the question
 * the list row cannot answer — it shows only the newest. This is where the rest
 * lives: each version's number, when it was written, how big it is, and a
 * download of its own, newest first.
 *
 * **The list is composed, not fetched.** Task 827 shipped no list-versions
 * route, and it does not need one: versions are numbered 1…`currentVersionNumber`
 * by construction, so the numbers are already known from the artifact and only
 * each version's metadata is read (`useArtifactVersions`). Those reads happen
 * only while the panel is open — history is detail, and detail should not cost a
 * request on every list render.
 */
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useOptionalToast } from '@/contexts/ToastContext';
import { useArtifactVersions, useDownloadArtifactVersion } from '@/hooks/useArtifacts';
import { ArtifactKindBadge } from './ArtifactKindBadge';
import { formatArtifactSize } from '@/types/artifacts';
import type { Artifact } from '@/types/artifacts';

interface ArtifactVersionHistoryPanelProps {
  open: boolean;
  onClose: () => void;
  /** The artifact whose history this is. Null closes the panel's content. */
  artifact: Artifact | null;
}

const formatTimestamp = (value: string | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export function ArtifactVersionHistoryPanel({
  open,
  onClose,
  artifact,
}: ArtifactVersionHistoryPanelProps) {
  const { versions, isLoading, isError } = useArtifactVersions(
    artifact?.id,
    artifact?.currentVersionNumber,
    { enabled: open }
  );
  const download = useDownloadArtifactVersion();
  const toast = useOptionalToast();

  const handleDownload = (versionNumber: number) => {
    if (!artifact) return;
    download.mutate(
      { artifactId: artifact.id, versionNumber, filename: artifact.name },
      {
        onError: () =>
          toast?.error('Could not download', 'That version could not be read.'),
      }
    );
  };

  return (
    <Dialog open={open && !!artifact} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          data-testid="artifact-version-history"
          className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="truncate text-lg font-semibold text-gray-900 dark:text-white">
                {artifact?.name}
              </DialogTitle>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {artifact && <ArtifactKindBadge kind={artifact.kind} />}
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                  {artifact?.contentType}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close version history"
              data-testid="artifact-version-history-close"
              className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-4">
            {isLoading ? (
              <p
                data-testid="artifact-versions-loading"
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                Loading versions…
              </p>
            ) : isError && versions.length === 0 ? (
              <p
                data-testid="artifact-versions-error"
                className="text-sm text-red-600 dark:text-red-400"
              >
                The version history could not be read. The artifact itself is unaffected.
              </p>
            ) : versions.length === 0 ? (
              <p
                data-testid="artifact-versions-empty"
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                This artifact has no versions to show.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {versions.map((version) => (
                  <li
                    key={version.id}
                    data-testid="artifact-version-row"
                    data-version-number={version.versionNumber}
                    className="flex flex-wrap items-center gap-3 py-3"
                  >
                    <span className="w-12 flex-shrink-0 text-sm font-medium text-gray-900 dark:text-white">
                      v{version.versionNumber}
                    </span>
                    <span className="min-w-0 flex-1 text-xs text-gray-500 dark:text-gray-400">
                      {formatTimestamp(version.createdAt)}
                    </span>
                    <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                      {formatArtifactSize(version.sizeBytes)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDownload(version.versionNumber)}
                      disabled={download.isPending}
                      data-testid="artifact-version-download"
                      aria-label={`Download version ${version.versionNumber}`}
                      className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      Download
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default ArtifactVersionHistoryPanel;
